import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AlertTriangle } from 'lucide-react';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { StatusChip } from '@/components/data/StatusChip';
import { Badge } from '@/components/ui/Badge';
import { ActionDialog } from '@/features/moderation/ActionDialog';
import { useModerationQueue } from '@/queries/moderation';
import { useApiError } from '@/lib/useApiError';
import {
  moderationStateSchema,
  type ModerationReport,
  type ModerationState,
} from '@/schemas/admin/moderation';
import { cn } from '@/lib/cn';

const searchSchema = z.object({
  state: moderationStateSchema.optional(),
  breached: z.string().optional(),
});

export const Route = createFileRoute('/_authed/moderation/queue')({
  validateSearch: searchSchema,
  component: ModerationQueueScreen,
});

function ModerationQueueScreen() {
  const { t } = useTranslation('moderation');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const state: ModerationState = search.state ?? 'open';
  const breachedOnly = search.breached === '1';
  const [actionTarget, setActionTarget] = React.useState<{
    subject: { kind: string; id: string };
    reportId: string;
  } | null>(null);

  const query = useModerationQueue(state);
  const errorInfo = query.error ? resolveError(query.error) : null;

  const filtered = React.useMemo(() => {
    if (!query.data) return [];
    if (!breachedOnly) return query.data;
    const now = Date.now();
    return query.data.filter((r) => r.sla_due_at && new Date(r.sla_due_at).getTime() < now);
  }, [query.data, breachedOnly]);

  const setState = React.useCallback(
    (next: ModerationState) => {
      void navigate({
        to: '/moderation/queue',
        search: (prev) => ({ ...prev, state: next }),
        replace: true,
      });
    },
    [navigate]
  );

  const columns: readonly DataColumn<ModerationReport>[] = [
    {
      id: 'sla',
      header: t('queue.columns.sla'),
      cell: (r) => {
        if (!r.sla_due_at) return <span>—</span>;
        const breached = new Date(r.sla_due_at).getTime() < Date.now();
        return (
          <span className="flex items-center gap-2">
            <DateTime
              value={r.sla_due_at}
              className={cn(breached && 'font-semibold text-[color:var(--color-danger)]')}
            />
            {breached ? (
              <Badge tone="danger" aria-label={t('queue.breached')}>
                <AlertTriangle aria-hidden className="size-3" />
                <span>{t('queue.breached')}</span>
              </Badge>
            ) : null}
          </span>
        );
      },
      sortBy: (r) => (r.sla_due_at ? new Date(r.sla_due_at).getTime() : Number.POSITIVE_INFINITY),
    },
    {
      id: 'subject',
      header: t('queue.columns.subject'),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
            {r.subject_kind}
          </span>
          {r.subject_kind === 'user' ? (
            <Link
              to="/users/$id"
              params={{ id: r.subject_id }}
              className="font-mono text-xs text-[color:var(--color-accent)] hover:underline"
              dir="ltr"
            >
              {r.subject_id.slice(0, 8)}…
            </Link>
          ) : (
            <span className="font-mono text-xs" dir="ltr">
              {r.subject_id.slice(0, 8)}…
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'category',
      header: t('queue.columns.category'),
      cell: (r) => <Badge tone="neutral">{r.category}</Badge>,
      sortBy: (r) => r.category,
    },
    {
      id: 'state',
      header: t('queue.columns.state'),
      cell: (r) => <StatusChip kind={(r.state as never) ?? 'open'} />,
    },
    {
      id: 'reporter',
      header: t('queue.columns.reporter'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-muted)]">
          {r.reporter_id ? `${r.reporter_id.slice(0, 8)}…` : '—'}
        </span>
      ),
      hiddenInitially: true,
    },
    {
      id: 'created',
      header: t('queue.columns.created'),
      cell: (r) => <DateTime value={r.created_at} />,
      sortBy: (r) => new Date(r.created_at).getTime(),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('queue.columns.actions')}</span>,
      cell: (r) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              setActionTarget({
                subject: { kind: r.subject_kind, id: r.subject_id },
                reportId: r.id,
              });
            }}
          >
            {t('queue.takeAction')}
          </Button>
        </div>
      ),
      align: 'end',
    },
  ];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('queue.title')}</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{t('queue.subtitle')}</p>
      </header>

      <Tabs value={state} onValueChange={(v) => setState(v as ModerationState)}>
        <TabsList aria-label={t('queue.tabsLabel')}>
          {moderationStateSchema.options.map((s) => (
            <TabsTrigger key={s} value={s}>
              {t(`queue.states.${s}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {breachedOnly ? (
        <div
          role="status"
          className="rounded-[var(--radius-md)] border border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)] px-3 py-2 text-xs text-[color:var(--color-fg)]"
        >
          {t('queue.breachedFilter')}{' '}
          <Link
            to="/moderation/queue"
            search={{ state }}
            className="underline"
          >
            {t('queue.breachedClear')}
          </Link>
        </div>
      ) : null}

      <DataTable<ModerationReport>
        data={filtered}
        columns={columns}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        error={errorInfo}
        empty={{
          title: t(`queue.empty.${state}.title`),
          body: t(`queue.empty.${state}.body`),
        }}
        caption={t('queue.cap200')}
        initialSort={{ id: 'sla', direction: 'asc' }}
      />

      {actionTarget ? (
        <ActionDialog
          open
          onOpenChange={(o) => {
            if (!o) setActionTarget(null);
          }}
          subject={actionTarget.subject}
          reportId={actionTarget.reportId}
          queueState={state}
        />
      ) : null}
    </div>
  );
}
